<template>
  <a-drawer
    :title="detail?.name"
    width="1000"
    :visible="visible"
    :closable="false"
    :footer-style="{ textAlign: 'right' }"
    @close="onClose"
  >
    <!-- 功能条 -->
    <div class="tool-view">
      <a-row class="tool-row">
        <a-col :span="18">
          <!-- 保留布局结构 -->
        </a-col>
        <a-col :span="6">
          <a-row>
            <!-- 操作 -->
            <a-col :span="6" class="tool-title"></a-col>
            <a-col :span="18" style="white-space: nowrap; text-align: right;">
              <a-button type="primary" @click="touchAdd"><plus-outlined /> 手动添加内容</a-button>
            </a-col>
          </a-row>
        </a-col>
      </a-row>
    </div>
    <!-- 列表 -->
    <a-table
      class="table-view"
      :data-source="dataSource"
      :columns="columns"
      :row-key="record => record.id"
      :pagination="pagination"
      :loading="isLoading"
      @change="handleTableChange"
    >
      <!-- 自定义行内容 -->
      <template #bodyCell="{ column, record }">
        <!-- 入库方式 -->
        <template v-if="column.key === 'type'">
          {{ record[column.key] === 1 ? '手动添加' : '后台创建' }}
        </template>
        <!-- 操作 -->
        <template v-else-if="column.key === 'operation'">
          <a class="operation-item" @click="touchEdit(record)">编辑</a>
          <a-popconfirm
            title="确定要删除这条内容吗？"
            ok-text="确定"
            cancel-text="取消"
            @confirm="touchDelete(record)"
          >
            <a class="operation-item">删除</a>
          </a-popconfirm>
        </template>
        <template v-else>
          {{ record[column.key] || '-' }}
        </template>
      </template>
    </a-table>
    
    <!-- 加载 -->
    <Loading :loading="isLoading"></Loading>
    <!-- 手动添加内容弹窗 -->
    <ContentAdd ref="RefContentAdd" @success="getList"></ContentAdd>
  </a-drawer>
</template>

<script setup>
import { onBeforeMount, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
// 引入 dayjs 及 扩展函数
import dayjs from 'dayjs'
// 引入公共方法
import Pub from '@/utils/public'
import Loading from '@/components/Loading'
// 引入油管内容弹窗组件
import ContentAdd from './ContentAdd'
// 引入 API
import { ytContentList, ytContentDelete } from '@/api/admin'

// emit
const emit = defineEmits(['success'])
// 油管内容弹窗引用
let RefContentAdd = ref(null)
// 显示
let visible = ref(false)
// 加载
let isLoading = ref(false)
// 修改对象
let detail = ref(null)
// 数据源
let dataSource = ref([])
// 分页信息
let pagination = reactive({
  total: 0,
  current: 1,
  pageSize: 10,
  showSizeChanger: true
})
// 列数据源 - 根据图片中的字段标题
let columns = ref([
  {
    title: '内容ID',
    dataIndex: 'content_id',
    key: 'content_id'
  },
  {
    title: '标题',
    dataIndex: 'name',
    key: 'name'
  },
  {
    title: '字幕语种',
    dataIndex: 'lang_name',
    key: 'lang_name'
  },
  {
    title: '账号',
    dataIndex: 'account_name',
    key: 'account_name'
  },
  {
    title: '公开日期',
    dataIndex: 'publish_date',
    key: 'publish_date'
  },
  {
    title: '入库方式',
    dataIndex: 'type',
    key: 'type'
  },
  {
    title: '翻译任务',
    dataIndex: 'task_id',
    key: 'task_id'
  },
  {
    title: '操作人',
    dataIndex: 'admin_name',
    key: 'admin_name'
  },
  {
    title: '操作',
    dataIndex: 'operation',
    key: 'operation',
    width: 130
  }
])

// 显示弹窗
function showDrawer(record) {
  visible.value = true
  detail.value = record
  // 初始化获取列表
  getList()
}

// 分页处理
function handleTableChange(p, filters, sorter) {
  pagination.current = p.current
  pagination.pageSize = p.pageSize
  // 获取列表
  getList()
}

// 新建
function touchAdd() {
  // 显示手动添加内容弹窗
  RefContentAdd.value.showDrawer({}, detail.value)
}

// 编辑
function touchEdit(record) {
  // 显示编辑弹窗，传入记录数据
  RefContentAdd.value.showDrawer(record, detail.value)
}

// 删除
function touchDelete(record) {
  isLoading.value = true
  ytContentDelete({ id: record.id }).then(res => {
    isLoading.value = false
    const { code, msg } = res
    if (code === 0) {
      message.success('删除成功')
      // 重新获取列表
      getList()
    } else {
      message.error(msg || '删除失败')
    }
  }).catch(() => {
    isLoading.value = false
    message.error('删除失败')
  })
}

// 获取列表
function getList() {
  isLoading.value = true
  const params = {
    yt_drama_id: detail.value.id,
    page: pagination.current,
    page_size: pagination.pageSize
  }
  // 暂时使用 YoutubeFilmlibrary 的接口
  ytContentList(params).then(res => {
    isLoading.value = false
    const { code, data } = res
    if (code === 0) {
      // 模拟数据，适配新的字段结构
      dataSource.value = data.data
      pagination.total = data.total
    }
  }).catch(() => {
    isLoading.value = false
  })
}

// 关闭弹窗
function onClose() {
  if (!isLoading.value) {
    visible.value = false
    detail.value = null
    // 重置分页
    pagination.current = 1
    pagination.total = 0
    dataSource.value = []
  }
}

// 暴露方法给父组件
defineExpose({
  showDrawer
})
</script>

<style scoped>
.tool-row {
  margin-bottom: 16px;
}

.tool-title {
  text-align: right;
}

.tool-item {
  width: 100%;
}

.table-view {
  background: #fff;
  border-radius: 6px;
}

.operation-item {
  color: #1890ff;
  margin-right: 16px;
  cursor: pointer;
}

.operation-item:hover {
  color: #40a9ff;
}
</style>
